import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {
  Badge,
  Box,
  Button,
  Divider,
  Dropdown,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  Stack,
  Typography,
} from '@mui/joy';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../../../hooks/useNotifications';
import { TNotification } from '../../../../redux/services/notifications/getNotifications';
import { markAllNotificationsAsRead } from '../../../../redux/services/notifications/markAllNotificationsAsRead';
import { patchNotification } from '../../../../redux/services/notifications/patchNotification';
import { NotificationDropdownItem } from './notificationItem';

const NOTIFICATIONS_TO_SHOW_WHEN_NOT_EXPANDED = 3;

export const NotificationDropdown = () => {
  const notificationsQuery = useNotifications();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [isNotificationListOpen, setIsNotificationListOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const notifications = notificationsQuery.data || [];

  const unreadNotifications = notifications.filter(
    (notification) => !notification.readStatus
  ).length;

  const notificationsToShow = expanded
    ? notifications
    : notifications.slice(0, NOTIFICATIONS_TO_SHOW_WHEN_NOT_EXPANDED);

  const onClickNotificationItem = async (item: TNotification) => {
    setIsNotificationListOpen(false);
    if (!item?.actionUrl) return;

    await patchNotification(item._id, {
      readStatus: true,
    });

    await notificationsQuery.refetch();

    navigate(item.actionUrl);
  };

  const onClickMarkAllNotificationsAsRead = async () => {
    try {
      setIsLoading(true);
      await markAllNotificationsAsRead();
      await notificationsQuery.refetch();
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Badge color="primary" badgeContent={unreadNotifications}>
      <Dropdown
        onOpenChange={(_, open) => setIsNotificationListOpen(open)}
      >
        <MenuButton
          slots={{ root: IconButton }}
          slotProps={{ root: { variant: 'plain', color: 'neutral' } }}
          onClick={() => setIsNotificationListOpen(!isNotificationListOpen)}
        >
          <NotificationsIcon />
        </MenuButton>
        <Menu
          open={isNotificationListOpen}
          sx={{
            zIndex: 99999999,
            maxHeight: expanded ? '100vh' : 'auto',
            overflowY: 'scroll',
            width: 'auto',
          }}
          popperOptions={{
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [0, 20],
                },
              },
            ],
          }}
        >
          <MenuItem onClick={() => setExpanded(!expanded)}>
            <Stack
              direction={'row'}
              justifyContent={'space-between'}
              alignItems={'center'}
              sx={{ width: '100%' }}
            >
              <Typography>Notificaties</Typography>
              <Button
                startDecorator={<DoneAllRoundedIcon />}
                variant="plain"
                onClick={onClickMarkAllNotificationsAsRead}
                disabled={isLoading}
              >
                Alles markeren als gelezen
              </Button>
            </Stack>
          </MenuItem>
          <Divider />
          {notificationsToShow.map((option) => {
            return (
              <NotificationDropdownItem
                key={option._id}
                item={option}
                onClick={() => onClickNotificationItem(option)}
                disabled={isLoading}
              />
            );
          })}
          <Divider />
          <MenuItem>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                setExpanded(true);
              }}
              variant="plain"
              sx={{
                paddingTop: '0.5rem',
              }}
            >
              {`Alle notificaties bekijken ${
                unreadNotifications > 0
                  ? `(${unreadNotifications} ongelezen)`
                  : ''
              }`}
            </Button>
          </MenuItem>
          {expanded && (
            <MenuItem>
              <Box sx={{ height: '4rem' }} />
            </MenuItem>
          )}
        </Menu>
      </Dropdown>
    </Badge>
  );
};
